// DCIC-06 v2.2 climate sections — the home of all climate data. Which
// section shows is driven by facility_type:
//   livestock_farm                                   → [climate]
//   biogas_system                                    → [biogas]
//   abattoir / slaughter_slab / processing / milk_collection_centre → [waste_recovery]
// Verification / approval / MRV+C statuses are system-set and read-only:
// they come from an approved Facility Status Update climate assessment or the
// MRV+C feed, never from this form.
import React, { useEffect, useMemo, useState } from 'react';
import { Field, Select, SectionHead, ReadonlyBox, INP, INP_LOCKED } from '../../reusable/formRecords/formFieldPrimitives';
import { facilitiesApi } from '../../../api/facilitiesApi';
import { livestockActorsApi } from '../../../api/workforceApi';
import { deriveFacilityClimateStatuses } from '../../../api/facilityStatusUpdatesApi';
import ProposeActorModal from '../../Workforce/ProposeActorModal';
import ProposeFacilityModal from './ProposeFacilityModal';
import {
  BIOGAS_ENERGY_USE_OPTIONS, BIOGAS_FEEDSTOCK_OPTIONS, BIOGAS_HOST_TYPE_OPTIONS, BIOGAS_SYSTEM_TYPE,
  CIRCULARITY_OPTIONS, CSA_PRACTICE_OPTIONS, FEEDING_REGIME_OPTIONS, LIVESTOCK_FARM_TYPE, MANURE_SYSTEM_OPTIONS,
  MRV_PARTICIPATION_OPTIONS, PRACTICE_VERIFICATION_OPTIONS, RECOVERY_STATUS_OPTIONS, RENEWABLE_ENERGY_OPTIONS,
  WASTE_APPROVAL_OPTIONS, WASTE_RECOVERY_FACILITY_TYPES, WASTE_RECOVERY_SYSTEM_OPTIONS,
  climateSectionFor, labelOf, labelsOf, toggleExclusiveNone,
} from './climateOptions';

const EMPTY_CLIMATE = {
  operator_actor_id: '', csa_practices_adopted: [], manure_management_system: '', feeding_regime: '',
  renewable_energy_on_site: [], biogas_system_ref: '', production_system_circularity: '',
};
const EMPTY_BIOGAS = {
  host_type: '', host_facility_id: '', digester_volume_m3: '', feedstock: [], energy_use: [], commissioning_date: '',
};
const EMPTY_WASTE = { waste_recovery_system: [], recovery_status: '', manure_waste_handling: '' };

export const EMPTY_CLIMATE_STATE = { climate: EMPTY_CLIMATE, biogas: EMPTY_BIOGAS, waste_recovery: EMPTY_WASTE };

const today = () => new Date().toISOString().slice(0, 10);
const arr = (value) => (Array.isArray(value) ? value : []);
const str = (value) => (value === null || value === undefined ? '' : String(value));

// Edit mode: seed the three sections from what the facility already holds.
export function readClimateState(row) {
  const c = row?.climate ?? {};
  const b = row?.biogas ?? {};
  const w = row?.waste_recovery ?? {};
  return {
    climate: {
      operator_actor_id: str(c.operator_actor_id),
      csa_practices_adopted: arr(c.csa_practices_adopted),
      manure_management_system: str(c.manure_management_system),
      feeding_regime: str(c.feeding_regime),
      renewable_energy_on_site: arr(c.renewable_energy_on_site),
      biogas_system_ref: str(c.biogas_system_ref),
      production_system_circularity: str(c.production_system_circularity),
    },
    biogas: {
      host_type: str(b.host_type),
      host_facility_id: str(b.host_facility_id),
      digester_volume_m3: str(b.digester_volume_m3),
      feedstock: arr(b.feedstock),
      energy_use: arr(b.energy_use),
      commissioning_date: str(b.commissioning_date),
    },
    waste_recovery: {
      waste_recovery_system: arr(w.waste_recovery_system),
      recovery_status: str(w.recovery_status),
      manure_waste_handling: str(w.manure_waste_handling),
    },
  };
}

export function isClimateStateValid(facilityType, state) {
  const section = climateSectionFor(facilityType);
  if (section === 'climate') {
    return state.climate.csa_practices_adopted.length > 0 && Boolean(state.climate.manure_management_system);
  }
  if (section === 'biogas') {
    const b = state.biogas;
    return Boolean(b.host_type && b.host_facility_id && Number(b.digester_volume_m3) > 0 && b.feedstock.length > 0
      && b.commissioning_date && b.commissioning_date <= today());
  }
  if (section === 'waste_recovery') {
    const w = state.waste_recovery;
    if (w.waste_recovery_system.length === 0) return false;
    return w.waste_recovery_system.includes('none') || Boolean(w.recovery_status);
  }
  return true;
}

const compact = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => !(v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0))));

// Returns the capacity_metadata with only the section that applies to this
// facility type — a type change never leaves stale climate data behind.
// System-set statuses already on the record are carried over untouched.
export function applyClimateMetadata(facilityType, state, metadata = {}) {
  const next = { ...metadata };
  delete next.climate;
  delete next.biogas;
  delete next.waste_recovery;
  const section = climateSectionFor(facilityType);
  if (section === 'climate') {
    const prev = metadata.climate ?? {};
    next.climate = compact({
      ...state.climate,
      mrv_participation_status: prev.mrv_participation_status,
    });
  } else if (section === 'biogas') {
    next.biogas = compact({ ...state.biogas, digester_volume_m3: state.biogas.digester_volume_m3 === '' ? '' : Number(state.biogas.digester_volume_m3) });
  } else if (section === 'waste_recovery') {
    const w = state.waste_recovery;
    next.waste_recovery = compact({ ...w, recovery_status: w.waste_recovery_system.includes('none') ? '' : w.recovery_status });
  }
  return next;
}

// ── Lookups ─────────────────────────────────────────────────────────────────
function useLookups(section) {
  const [actors, setActors] = useState([]);
  const [facilities, setFacilities] = useState([]);
  useEffect(() => {
    if (!section) return undefined;
    let active = true;
    facilitiesApi.listAll({ per_page: 500 })
      .then(({ items }) => { if (active) setFacilities(items ?? []); })
      .catch(() => { if (active) setFacilities([]); });
    if (section === 'climate') {
      livestockActorsApi.listAll({ per_page: 500 })
        .then(({ items }) => { if (active) setActors(items ?? []); })
        .catch(() => { if (active) setActors([]); });
    }
    return () => { active = false; };
  }, [section]);
  return { actors, facilities, setActors, setFacilities };
}

const facilityOption = (f) => ({ value: f.uuid ?? f.id, label: [f.name, f.lga, f.state].filter(Boolean).join(' — ') || f.uuid });

function RegistryLookup({ label, required, hint, value, onChange, options, placeholder, onPropose, disabled }) {
  return (
    <Field label={label} required={required} hint={hint}>
      <div className="flex gap-2">
        <div className="flex-1">
          <Select value={value} onChange={onChange} options={options} placeholder={placeholder} disabled={disabled} className={disabled ? INP_LOCKED : INP} />
        </div>
        {!disabled && onPropose && (
          <button type="button" onClick={onPropose}
            className="shrink-0 rounded-lg border border-dashed border-green-300 bg-green-50 px-4 py-3 text-[12px] font-semibold text-green-700 hover:bg-green-100">
            + Propose
          </button>
        )}
      </div>
    </Field>
  );
}

function ChoiceChips({ options, values, onToggle, disabled }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const on = values.includes(option.value);
        return (
          <button key={option.value} type="button" disabled={disabled} onClick={() => onToggle(option.value)}
            className={`rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-all disabled:cursor-not-allowed ${on
              ? 'border-green-400 bg-green-50 text-green-700'
              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function ClimatePanel({ title, note, children }) {
  return (
    <div className="space-y-4 rounded-2xl border border-green-200 bg-green-50/40 p-4">
      <div>
        <SectionHead title={title} />
        {note && <p className="mt-1 text-xs text-green-700">{note}</p>}
      </div>
      {children}
    </div>
  );
}

// ── Form sections ───────────────────────────────────────────────────────────
export default function FacilityClimateSections({ facilityType, facilityUuid, state, onChange, locked = false, metadata = {} }) {
  const section = climateSectionFor(facilityType);
  const { actors, facilities, setActors, setFacilities } = useLookups(section);
  const [proposeActor, setProposeActor] = useState(false);
  const [proposeFacility, setProposeFacility] = useState(null); // { target, allowedTypes }
  const systemStatuses = useMemo(() => deriveFacilityClimateStatuses(facilityUuid), [facilityUuid]);

  if (!section) return null;
  const fieldCls = locked ? INP_LOCKED : INP;
  const setIn = (key, field) => (e) => {
    const value = e?.target ? e.target.value : e;
    onChange({ ...state, [key]: { ...state[key], [field]: value } });
  };
  const toggleIn = (key, field, exclusiveNone) => (value) => {
    const current = state[key][field];
    const next = exclusiveNone ? toggleExclusiveNone(current, value) : (current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
    onChange({ ...state, [key]: { ...state[key], [field]: next } });
  };

  const others = facilities.filter((f) => (f.uuid ?? f.id) !== facilityUuid);
  const biogasOptions = others.filter((f) => f.facility_type === BIOGAS_SYSTEM_TYPE).map(facilityOption);
  const hostTypes = [LIVESTOCK_FARM_TYPE, ...WASTE_RECOVERY_FACILITY_TYPES];
  const hostOptions = others.filter((f) => hostTypes.includes(f.facility_type)).map(facilityOption);
  const actorOptions = actors.map((a) => ({ value: a.uuid ?? a.id, label: [a.fullName ?? a.full_name, a.actorType].filter(Boolean).join(' — ') }));

  const onFacilityProposed = (record) => {
    const uuid = record?.uuid ?? record?.id;
    if (!uuid || !proposeFacility) return;
    setFacilities((prev) => [...prev, record]);
    if (proposeFacility.target === 'biogas_system_ref') setIn('climate', 'biogas_system_ref')(uuid);
    else setIn('biogas', 'host_facility_id')(uuid);
  };

  return (
    <>
      {section === 'climate' && (
        <ClimatePanel title="Climate — Livestock Farm" note="Climate data is recorded on the farm facility. Values come from the approved climate reference lists.">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Field label="Climate-Smart Practices Adopted" required hint="Select all that apply. “None” is an explicit answer and cannot be combined with other practices.">
                <ChoiceChips options={CSA_PRACTICE_OPTIONS} values={state.climate.csa_practices_adopted} onToggle={toggleIn('climate', 'csa_practices_adopted', true)} disabled={locked} />
              </Field>
            </div>
            <Field label="Manure Management System" required>
              <Select value={state.climate.manure_management_system} onChange={setIn('climate', 'manure_management_system')} options={MANURE_SYSTEM_OPTIONS} disabled={locked} placeholder="Select manure system" className={fieldCls} />
            </Field>
            <Field label="Feeding Regime">
              <Select value={state.climate.feeding_regime} onChange={setIn('climate', 'feeding_regime')} options={FEEDING_REGIME_OPTIONS} disabled={locked} placeholder="Select feeding regime" className={fieldCls} />
            </Field>
            <div className="col-span-2">
              <Field label="Renewable Energy On Site" hint="“None” cannot be combined with other sources.">
                <ChoiceChips options={RENEWABLE_ENERGY_OPTIONS} values={state.climate.renewable_energy_on_site} onToggle={toggleIn('climate', 'renewable_energy_on_site', true)} disabled={locked} />
              </Field>
            </div>
            <RegistryLookup label="Biogas System on this Farm" hint="Link only — the digester is registered as its own Biogas System facility."
              value={state.climate.biogas_system_ref} onChange={setIn('climate', 'biogas_system_ref')} options={biogasOptions}
              placeholder="Select from registry" disabled={locked}
              onPropose={() => setProposeFacility({ target: 'biogas_system_ref', allowedTypes: [BIOGAS_SYSTEM_TYPE] })} />
            <Field label="Production System Circularity" hint="Declared — counts only once verified by a climate assessment.">
              <Select value={state.climate.production_system_circularity} onChange={setIn('climate', 'production_system_circularity')} options={CIRCULARITY_OPTIONS} disabled={locked} placeholder="Select circularity" className={fieldCls} />
            </Field>
            <div className="col-span-2">
              <RegistryLookup label="Farm Owner / Operator" hint="Link only — no climate data is stored on the person."
                value={state.climate.operator_actor_id} onChange={setIn('climate', 'operator_actor_id')} options={actorOptions}
                placeholder="Select from registry" disabled={locked} onPropose={() => setProposeActor(true)} />
            </div>
            <Field label="Practice Verification Status" hint="System-set by an approved climate assessment.">
              <ReadonlyBox>{labelOf(PRACTICE_VERIFICATION_OPTIONS, systemStatuses.practice_verification_status)}</ReadonlyBox>
            </Field>
            <Field label="MRV+C Participation Status" hint="System-set by the national MRV+C data feed.">
              <ReadonlyBox>{metadata?.climate?.mrv_participation_status ? labelOf(MRV_PARTICIPATION_OPTIONS, metadata.climate.mrv_participation_status) : 'Awaiting MRV+C feed'}</ReadonlyBox>
            </Field>
          </div>
        </ClimatePanel>
      )}

      {section === 'biogas' && (
        <ClimatePanel title="Biogas System" note="Each digester is its own facility, linked to the farm or processing facility it serves.">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Host Type" required>
              <Select value={state.biogas.host_type} onChange={setIn('biogas', 'host_type')} options={BIOGAS_HOST_TYPE_OPTIONS} disabled={locked} placeholder="Select host type" className={fieldCls} />
            </Field>
            <RegistryLookup label="Facility Served" required hint="The livestock farm or processing facility this digester serves."
              value={state.biogas.host_facility_id} onChange={setIn('biogas', 'host_facility_id')} options={hostOptions}
              placeholder="Select from registry" disabled={locked}
              onPropose={() => setProposeFacility({ target: 'host_facility_id', allowedTypes: hostTypes })} />
            <Field label="Digester Volume (m³)" required hint="Must be greater than 0.">
              <input type="number" min="0" step="any" value={state.biogas.digester_volume_m3} onChange={setIn('biogas', 'digester_volume_m3')} disabled={locked} placeholder="e.g. 12" className={fieldCls} />
            </Field>
            <Field label="Commissioning Date" required hint="Cannot be in the future.">
              <input type="date" max={today()} value={state.biogas.commissioning_date} onChange={setIn('biogas', 'commissioning_date')} disabled={locked} className={fieldCls} />
            </Field>
            <div className="col-span-2">
              <Field label="Feedstock" required hint="Select all that go into the digester.">
                <ChoiceChips options={BIOGAS_FEEDSTOCK_OPTIONS} values={state.biogas.feedstock} onToggle={toggleIn('biogas', 'feedstock', false)} disabled={locked} />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label="Energy Use">
                <ChoiceChips options={BIOGAS_ENERGY_USE_OPTIONS} values={state.biogas.energy_use} onToggle={toggleIn('biogas', 'energy_use', false)} disabled={locked} />
              </Field>
            </div>
          </div>
        </ClimatePanel>
      )}

      {section === 'waste_recovery' && (
        <ClimatePanel title="Waste Recovery" note="Circular waste-recovery at this processing facility.">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Field label="Waste Recovery System" required hint="“None” is an explicit answer and cannot be combined with other systems.">
                <ChoiceChips options={WASTE_RECOVERY_SYSTEM_OPTIONS} values={state.waste_recovery.waste_recovery_system} onToggle={toggleIn('waste_recovery', 'waste_recovery_system', true)} disabled={locked} />
              </Field>
            </div>
            {!state.waste_recovery.waste_recovery_system.includes('none') && (
              <Field label="Recovery Status" required>
                <Select value={state.waste_recovery.recovery_status} onChange={setIn('waste_recovery', 'recovery_status')} options={RECOVERY_STATUS_OPTIONS} disabled={locked} placeholder="Select recovery status" className={fieldCls} />
              </Field>
            )}
            <Field label="Lairage / Pen Manure Handling">
              <Select value={state.waste_recovery.manure_waste_handling} onChange={setIn('waste_recovery', 'manure_waste_handling')} options={MANURE_SYSTEM_OPTIONS} disabled={locked} placeholder="Select manure handling" className={fieldCls} />
            </Field>
            <Field label="Waste Recovery Approval Status" hint="System-set by an approved climate assessment or inspection.">
              <ReadonlyBox>{labelOf(WASTE_APPROVAL_OPTIONS, systemStatuses.approval_status)}</ReadonlyBox>
            </Field>
          </div>
        </ClimatePanel>
      )}

      <ProposeActorModal
        isOpen={proposeActor}
        onClose={() => setProposeActor(false)}
        onCreated={(actor) => {
          const uuid = actor?.uuid ?? actor?.id;
          if (!uuid) return;
          setActors((prev) => [...prev, actor]);
          setIn('climate', 'operator_actor_id')(uuid);
        }}
      />
      <ProposeFacilityModal
        isOpen={Boolean(proposeFacility)}
        allowedTypes={proposeFacility?.allowedTypes}
        onClose={() => setProposeFacility(null)}
        onCreated={onFacilityProposed}
      />
    </>
  );
}

// ── Review / view rows ──────────────────────────────────────────────────────
export function FacilityClimateReview({ facilityType, facilityUuid, state, metadata = {}, ReviewRow, lookupLabel = (v) => v }) {
  const section = climateSectionFor(facilityType);
  if (!section) return null;
  const statuses = deriveFacilityClimateStatuses(facilityUuid);
  if (section === 'climate') {
    const c = state.climate;
    return (
      <>
        <SectionHead title="Climate — Livestock Farm" />
        <ReviewRow label="Climate-Smart Practices Adopted" value={labelsOf(CSA_PRACTICE_OPTIONS, c.csa_practices_adopted)} />
        <ReviewRow label="Manure Management System" value={labelOf(MANURE_SYSTEM_OPTIONS, c.manure_management_system)} />
        <ReviewRow label="Feeding Regime" value={labelOf(FEEDING_REGIME_OPTIONS, c.feeding_regime)} />
        <ReviewRow label="Renewable Energy On Site" value={labelsOf(RENEWABLE_ENERGY_OPTIONS, c.renewable_energy_on_site)} />
        <ReviewRow label="Biogas System on this Farm" value={lookupLabel(c.biogas_system_ref)} />
        <ReviewRow label="Production System Circularity" value={labelOf(CIRCULARITY_OPTIONS, c.production_system_circularity)} />
        <ReviewRow label="Farm Owner / Operator" value={lookupLabel(c.operator_actor_id)} />
        <ReviewRow label="Practice Verification Status" value={labelOf(PRACTICE_VERIFICATION_OPTIONS, statuses.practice_verification_status)} />
        <ReviewRow label="MRV+C Participation Status" value={metadata?.climate?.mrv_participation_status ? labelOf(MRV_PARTICIPATION_OPTIONS, metadata.climate.mrv_participation_status) : 'Awaiting MRV+C feed'} />
      </>
    );
  }
  if (section === 'biogas') {
    const b = state.biogas;
    return (
      <>
        <SectionHead title="Biogas System" />
        <ReviewRow label="Host Type" value={labelOf(BIOGAS_HOST_TYPE_OPTIONS, b.host_type)} />
        <ReviewRow label="Facility Served" value={lookupLabel(b.host_facility_id)} />
        <ReviewRow label="Digester Volume (m³)" value={b.digester_volume_m3} />
        <ReviewRow label="Feedstock" value={labelsOf(BIOGAS_FEEDSTOCK_OPTIONS, b.feedstock)} />
        <ReviewRow label="Energy Use" value={labelsOf(BIOGAS_ENERGY_USE_OPTIONS, b.energy_use)} />
        <ReviewRow label="Commissioning Date" value={b.commissioning_date} />
      </>
    );
  }
  const w = state.waste_recovery;
  return (
    <>
      <SectionHead title="Waste Recovery" />
      <ReviewRow label="Waste Recovery System" value={labelsOf(WASTE_RECOVERY_SYSTEM_OPTIONS, w.waste_recovery_system)} />
      {!w.waste_recovery_system.includes('none') && <ReviewRow label="Recovery Status" value={labelOf(RECOVERY_STATUS_OPTIONS, w.recovery_status)} />}
      <ReviewRow label="Lairage / Pen Manure Handling" value={labelOf(MANURE_SYSTEM_OPTIONS, w.manure_waste_handling)} />
      <ReviewRow label="Waste Recovery Approval Status" value={labelOf(WASTE_APPROVAL_OPTIONS, statuses.approval_status)} />
    </>
  );
}

// Resolves facility / actor uuids in the review step to readable names.
export function useClimateLookupLabels(facilityType) {
  const section = climateSectionFor(facilityType);
  const { actors, facilities } = useLookups(section);
  return useMemo(() => {
    const map = new Map();
    facilities.forEach((f) => map.set(f.uuid ?? f.id, facilityOption(f).label));
    actors.forEach((a) => map.set(a.uuid ?? a.id, a.fullName ?? a.full_name));
    return (value) => (value ? (map.get(value) ?? value) : '');
  }, [actors, facilities]);
}
